import { useRef, useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import logo from '../traderhub24-logo.svg'
import { useAuth } from './contexts/AuthContext'
import { supabase } from './lib/supabase'
import Footer from './components/Footer'
import Login from './components/Login'
import Register from './components/Register'

type PageKey =
  | 'home'
  | 'changelog'
  | 'privacy-policy'
  | 'terms-and-conditions'
  | 'risk-warning'
  | 'documentation'
  | 'all-trades'
  | 'analytics'
  | 'tools'
  | 'strategies'
  | 'account'
  | 'symbol-management'
  | 'social-trade'
  | 'social-trade-trade-pools'
  | 'social-trade-followers'
  | 'community'

type CommunityTradeRow = {
  id: string
  user_id: string
  profile_name: string | null
  symbol: string
  market: 'Commodity' | 'Crypto' | 'Forex' | 'Stock'
  long_short: 'long' | 'short'
  entry_date: string | null
  exit_date: string | null
  pnl: number | null
  pnl_percentage: number | null
  currency: string | null
  public_slug: string | null
  trade_pool: string | null
  public_visibility?: string | null
}

type FollowingTraderRow = {
  trader_user_id: string
  profile_name: string | null
  is_global: boolean
  pools_followed: number
  followed_at: string
}

function formatShortDate(dateString: string | null) {
  if (!dateString) return '-'
  const d = new Date(dateString)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  })
}

function formatRelativeTime(dateString: string | null) {
  if (!dateString) return '-'
  const d = new Date(dateString)
  if (Number.isNaN(d.getTime())) return '-'
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return formatShortDate(dateString)
}

function Community({
  onBack,
  setCurrentPage,
  embedded,
}: {
  onBack: () => void
  setCurrentPage: (page: PageKey) => void
  embedded?: boolean
}) {
  const { t } = useTranslation('pages')
  const navigate = useNavigate()
  const { user, userProfile, signOut, loading: authLoading } = useAuth()
  const [showLogin, setShowLogin] = useState(false)
  const [showRegister, setShowRegister] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [allTrades, setAllTrades] = useState<CommunityTradeRow[]>([])
  const [followingTrades, setFollowingTrades] = useState<CommunityTradeRow[]>([])
  const [followingTraders, setFollowingTraders] = useState<FollowingTraderRow[]>([])
  const [activeTab, setActiveTab] = useState<'following' | 'all'>('following')

  const [filterMarket, setFilterMarket] = useState<'all' | CommunityTradeRow['market']>('all')
  const [filterDirection, setFilterDirection] = useState<'all' | CommunityTradeRow['long_short']>('all')
  const [search, setSearch] = useState('')

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUserMenu])

  // Fetch all community trades
  const fetchAllCommunityTrades = async () => {
    try {
      const { data, error: rpcError } = await supabase.rpc('get_community_trades', { p_limit: 100 })
      if (rpcError) throw rpcError
      setAllTrades(((data as any[]) || []) as CommunityTradeRow[])
    } catch (err: any) {
      console.error('Failed to fetch all community trades:', err)
    }
  }

  // Fetch trades from traders you follow (pool-matched on the server)
  const fetchFollowingTrades = async () => {
    if (!user) {
      setFollowingTrades([])
      return
    }

    try {
      const { data, error: rpcError } = await supabase.rpc('get_following_community_trades', { p_limit: 100 })
      if (rpcError) throw rpcError
      setFollowingTrades(((data as any[]) || []) as CommunityTradeRow[])
    } catch (err: any) {
      console.error('Failed to fetch following trades:', err)
      setFollowingTrades([])
    }
  }

  // Fetch traders you follow (for the Following tab header)
  const fetchFollowingTraders = async () => {
    if (!user) {
      setFollowingTraders([])
      return
    }
    try {
      const { data, error: rpcError } = await supabase.rpc('get_following_traders')
      if (rpcError) throw rpcError
      setFollowingTraders(((data as any[]) || []) as FollowingTraderRow[])
    } catch (err: any) {
      console.error('Failed to fetch following traders:', err)
      setFollowingTraders([])
    }
  }

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      setError(null)

      // Non-auth users can still browse the all-community feed.
      if (!user) {
        setActiveTab('all')
        await fetchAllCommunityTrades()
        setFollowingTrades([])
        setFollowingTraders([])
        setLoading(false)
        return
      }

      await Promise.all([fetchAllCommunityTrades(), fetchFollowingTrades(), fetchFollowingTraders()])
      setLoading(false)
    }
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const currentTrades = activeTab === 'following' ? followingTrades : allTrades

  const handleOpenPublicTrade = (trade: CommunityTradeRow) => {
    // Community visibility trades intentionally have no public_slug (see migration 027).
    // For those, we open the same read-only PublicTrade page using the trade UUID.
    // PublicTrade will fetch via a read-only RPC (no edit capabilities).
    const token = trade.public_slug ?? trade.id
    navigate(`/p/${token}`)
  }

  const filteredItems = useMemo(() => {
    let rows = [...currentTrades]

    if (filterMarket !== 'all') {
      rows = rows.filter((t) => t.market === filterMarket)
    }
    if (filterDirection !== 'all') {
      rows = rows.filter((t) => t.long_short === filterDirection)
    }
    if (search.trim()) {
      const s = search.trim().toLowerCase()
      rows = rows.filter(
        (t) =>
          t.symbol.toLowerCase().includes(s) ||
          (t.profile_name || '').toLowerCase().includes(s),
      )
    }

    return rows
  }, [currentTrades, filterMarket, filterDirection, search])

  return (
    <div className={embedded ? "w-full" : "min-h-screen bg-app-bg text-gray-900"}>
      {!embedded && (
      <header className="w-full py-3 px-4 md:px-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <img
            src={logo}
            alt="TraderHub24"
            className="h-12 md:h-20 w-auto cursor-pointer hover:opacity-80 transition-opacity"
            onClick={onBack}
          />
          <div className="flex items-center gap-2">
            {authLoading ? (
              <div className="text-sm text-gray-500">Loading...</div>
            ) : (
              <div className="relative flex items-center gap-2">
                {/* Mobile Menu Button */}
                {user && (
                  <button
                    onClick={() => setShowMobileMenu(!showMobileMenu)}
                    className="md:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-300"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {showMobileMenu ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                      )}
                    </svg>
                  </button>
                )}

                {user ? (
                  <div className="relative" ref={userMenuRef}>
                    <button
                      onClick={() => setShowUserMenu(!showUserMenu)}
                      className="px-3 md:px-4 py-2 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2 border border-gray-300"
                      title="Account"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      <span className="hidden md:inline">Account</span>
                    </button>
                    {showUserMenu && (
                      <div className="absolute right-0 mt-2 w-64 bg-app-box rounded-lg shadow-lg border border-gray-200 z-50">
                        <div className="p-4 border-b border-gray-200">
                          <p className="text-sm font-semibold text-gray-900">Account</p>
                          <p className="text-sm text-gray-600 mt-1 truncate">{user.email}</p>
                        </div>
                        <div className="p-2">
                          {userProfile?.is_admin && (
                            <button
                              onClick={() => {
                                setCurrentPage('symbol-management')
                                setShowUserMenu(false)
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                              Symbol Management
                              <span className="ml-auto text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded">
                                Admin
                              </span>
                            </button>
                          )}
                          <button
                            onClick={() => {
                              navigate("/app/account")
                              setShowUserMenu(false)
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4a4 4 0 100 8 4 4 0 000-8zM6 20a6 6 0 0112 0"
                              />
                            </svg>
                            Account Settings
                          </button>
                          <button
                            onClick={() => {
                              signOut()
                              setShowUserMenu(false)
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                              />
                            </svg>
                            Sign Out
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => setShowLogin(true)}
                    className="px-4 py-2 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2 border border-gray-300"
                    title="Sign In"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                      />
                    </svg>
                    Sign In
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Mobile Sidebar/Menu */}
        {showMobileMenu && user && (
          <div className="md:hidden fixed inset-0 z-[100] flex">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowMobileMenu(false)} />
            <div className="relative w-64 max-w-[80%] h-full bg-app-box shadow-xl flex flex-col p-4 animate-in slide-in-from-left duration-300">
              <div className="flex items-center justify-between mb-8">
                <img src={logo} alt="TraderHub24" className="h-8 w-auto" />
                <button onClick={() => setShowMobileMenu(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <nav className="space-y-2">
                {[
                  { id: "all-trades", label: "All Trades", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" },
                  { id: "analytics", label: "Analytics", icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" },
                  { id: "tools", label: "Tools", icon: "M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a1 1 0 01-1-1v-3a1 1 0 011-1h1a2 2 0 100-4H4a1 1 0 01-1-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" },
                  { id: "strategies", label: "Strategies", icon: "M13 10V3L4 14h7v7l9-11h-7z" },
                  { id: "social-trade", label: "Social Trade", icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" },
                  { id: "community", label: "Community", icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" },
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setCurrentPage(item.id as any);
                      setShowMobileMenu(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      item.id === "community" 
                        ? "bg-orange-50 text-orange-600" 
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                    </svg>
                    {item.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        )}
      </header>
      )}

      {(() => {
        const W = embedded ? "div" : "main";
        return (
        <W className={embedded ? "dashboard-page-content" : "max-w-6xl mx-auto px-4 md:px-6 py-6"}>
        {!embedded && user && (
          <section className="hidden md:flex items-center justify-center gap-4 mb-6">
            <button
              onClick={() => setCurrentPage("home")}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors flex items-center justify-center"
              title="Home"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </button>
            <button
              onClick={() => navigate("/app/all-trades")}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
            >
              All Trades
            </button>
            <button
              onClick={() => navigate("/app/analytics")}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
            >
              Analytics
            </button>
            <button
              onClick={() => navigate("/app/tools")}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
            >
              Tools
            </button>
            <button
              onClick={() => navigate("/app/strategies")}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
            >
              Strategies
            </button>
            <button
              onClick={() => navigate("/app/social-trade")}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
            >
              Social Trade
            </button>
            <button
              onClick={() => navigate("/app/community")}
              className="px-6 py-2 text-sm font-medium text-white bg-orange-500 border border-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
            >
              Community
            </button>
          </section>
        )}

        {/* Main Feed */}
        <div>
            <div className="mb-6">
              <h1 className="text-3xl font-bold">{t('community.title')}</h1>
              <p className="text-gray-600 mt-2">{t('community.subtitle')}</p>
            </div>

            {/* Tabs */}
            {user && (
              <div className="flex gap-2 mb-6 border-b border-gray-200">
                <button
                  onClick={() => setActiveTab('following')}
                  className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                    activeTab === 'following'
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Following ({followingTrades.length})
                </button>
                <button
                  onClick={() => setActiveTab('all')}
                  className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                    activeTab === 'all'
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  All Community ({allTrades.length})
                </button>
              </div>
            )}

            {/* Following traders list (only in Following tab) */}
            {user && activeTab === 'following' && (
              <div className="bg-app-box border border-gray-200 rounded-2xl p-4 mb-6">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Following traders</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Your feed includes pool-specific trades only when the trade’s pool matches the follower pool.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      fetchFollowingTraders()
                      fetchFollowingTrades()
                    }}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Refresh
                  </button>
                </div>

                {followingTraders.length === 0 ? (
                  <div className="mt-3 text-xs text-gray-500">
                    You’re not following anyone yet. To follow a trader, ask them to add your email to their Followers / Trade Pool followers list.
                  </div>
                ) : (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {followingTraders.map((t) => {
                      const displayName = t.profile_name || 'Anonymous'
                      const initial = (displayName[0] || 'A').toUpperCase()
                      const scopeLabel = t.is_global
                        ? 'Global'
                        : t.pools_followed > 0
                          ? `${t.pools_followed} pool`
                          : 'Pool'
                      return (
                        <div
                          key={t.trader_user_id}
                          className="flex items-center gap-2 px-3 py-2 rounded-full bg-white border border-gray-200"
                          title={`Follow scope: ${scopeLabel}`}
                        >
                          <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center">
                            <span className="text-orange-700 font-semibold text-xs">{initial}</span>
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs font-semibold text-gray-900 truncate max-w-[140px]">{displayName}</div>
                            <div className="text-[11px] text-gray-500">{scopeLabel}</div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Filters */}
            <div className="bg-app-box border border-gray-200 rounded-2xl p-4 mb-6">
              <div className="flex items-center gap-3 flex-wrap">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Market</label>
                  <select
                    value={filterMarket}
                    onChange={(e) => setFilterMarket(e.target.value as any)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="all">All</option>
                    <option value="Commodity">Commodity</option>
                    <option value="Crypto">Crypto</option>
                    <option value="Forex">Forex</option>
                    <option value="Stock">Stock</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Direction</label>
                  <select
                    value={filterDirection}
                    onChange={(e) => setFilterDirection(e.target.value as any)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="all">All</option>
                    <option value="long">Long</option>
                    <option value="short">Short</option>
                  </select>
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Symbol or trader name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>
            </div>

            {/* Feed */}
            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Loading trades...</p>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="text-center py-12 bg-app-box border border-gray-200 rounded-2xl">
                <svg
                  className="w-16 h-16 mx-auto text-gray-400 mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p className="text-gray-600 font-medium">No trades found</p>
                <p className="text-sm text-gray-500 mt-2">
                  {activeTab === 'following'
                    ? "You're not following any traders yet. Add your email to traders' follower lists in Social Trade."
                    : 'Be the first to share a trade with the community!'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredItems.map((trade) => (
                  <div
                    key={trade.id}
                    onClick={() => handleOpenPublicTrade(trade)}
                    className="bg-app-box border border-gray-200 rounded-2xl p-6 hover:shadow-md transition-shadow cursor-pointer"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handleOpenPublicTrade(trade)
                      }
                    }}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                          <span className="text-orange-600 font-semibold text-sm">
                            {(trade.profile_name || 'A')[0].toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {trade.profile_name || 'Anonymous Trader'}
                          </p>
                          <p className="text-xs text-gray-500">{formatRelativeTime(trade.entry_date)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            trade.long_short === 'long'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {trade.long_short.toUpperCase()}
                        </span>
                        <span className="px-2 py-1 rounded text-xs font-medium text-gray-600 bg-gray-100">
                          {trade.market}
                        </span>
                        {trade.trade_pool && (
                          <span className="px-2 py-1 rounded text-xs font-medium text-gray-600 bg-gray-100">
                            Pool
                          </span>
                        )}
                        {activeTab === 'following' && (
                          <span className="px-2 py-1 rounded text-xs font-medium text-gray-600 bg-gray-100">
                            Followers
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="mb-4">
                      <h3 className="text-xl font-bold text-gray-900 mb-1">{trade.symbol}</h3>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span>Entry: {formatShortDate(trade.entry_date)}</span>
                        {trade.exit_date && <span>Exit: {formatShortDate(trade.exit_date)}</span>}
                        <span className={trade.exit_date ? 'text-gray-400' : 'text-green-600 font-medium'}>
                          {trade.exit_date ? 'CLOSED' : 'OPEN'}
                        </span>
                      </div>
                    </div>

                    {trade.pnl !== null && (
                      <div className="mb-4 p-3 bg-white rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">P&L</span>
                          <div className="text-right">
                            <span
                              className={`text-lg font-bold ${
                                trade.pnl >= 0 ? 'text-green-600' : 'text-red-600'
                              }`}
                            >
                              {trade.pnl >= 0 ? '+' : ''}
                              {trade.pnl.toFixed(2)} {trade.currency ?? ''}
                            </span>
                            {trade.pnl_percentage !== null && (
                              <div className="text-xs text-gray-500 mt-0.5">
                                {trade.pnl_percentage >= 0 ? '+' : ''}
                                {trade.pnl_percentage.toFixed(2)}%
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="text-xs text-gray-500">
                      {activeTab === 'all'
                        ? 'Shared to community.'
                        : 'Shared to followers (pool-matched).'}
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
        </W>
        );
      })()}

      <Footer
        currentPage="community"
        onNavigate={(page) => {
          if (page === 'privacy-policy') navigate('/privacy')
          else if (page === 'terms-and-conditions') navigate('/terms')
          else if (page === 'risk-warning') navigate('/risk-warning')
          else if (page === 'documentation') navigate('/documentation')
          else if (page === 'contact') navigate('/contact')
          else setCurrentPage(page)
        }}
      />

      {!embedded && showLogin && (
        <Login
          onClose={() => setShowLogin(false)}
          onSwitchToRegister={() => {
            setShowLogin(false)
            setShowRegister(true)
          }}
        />
      )}
      {!embedded && showRegister && (
        <Register
          onClose={() => setShowRegister(false)}
          onSwitchToLogin={() => {
            setShowRegister(false)
            setShowLogin(true)
          }}
        />
      )}
    </div>
  )
}

export default Community
